import * as React from 'react';
import {
    TableRow,
    TableCell,
    IconButton,
    Collapse,
    Box,
    Table,
    TableBody,
    Avatar,
    CircularProgress,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DeleteIcon from '@mui/icons-material/Delete';
import * as PXBColors from '@brightlayer-ui/colors';
import { pushAppRoute } from '../../data/domain/route-manager';
import { useHistory } from 'react-router-dom';
import { isRecordFound, useUserRoles } from '../../hooks/data-hooks';
import { Status, UserOperations, UserRole } from '../../types';
import { useSelector } from 'react-redux';
import { Selectors } from '../../selectors';
import userStyles from '../../app/user-management/UserStyle';
import { useAppRoute } from '../../hooks/app-route-hooks';
import { StatusTag } from '../StatusTag/StatusTag';

const useStyles = makeStyles({
    tableRow: {
        backgroundColor: PXBColors.white[50],
    },
    hover: {},
    iconVisibilityHidden: {
        visibility: 'hidden',
    },
    iconVisibility: {
        visibility: 'visible',
    },
});

function userTableData(
    userId: string,
    firstName: string,
    lastName: string,
    location: string,
    role: string,
    email: string,
    status: string,
    roles: any // eslint-disable-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
    return {
        userId,
        firstName,
        lastName,
        location,
        role,
        email,
        status,
        roles,
    };
}

export const Row = (props: {
    loggedInUser: string;
    row: ReturnType<typeof userTableData>;
    index: number;
    deleteClick: Function;
    deleteIndex: number;
}): JSX.Element => {
    const classes = useStyles();
    const userClasses = userStyles();
    const { row } = props;
    const [open, setOpen] = React.useState(false);
    const [showActions, setShowActions] = React.useState(false);
    const history = useHistory();
    const userRole = useUserRoles();
    const registrationState = useSelector(Selectors.Registration.registration);
    const currentAppRoute = useAppRoute('ManageUser');

    const labelId = `enhanced-table-checkbox-${props.index}`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleDelete = (userId: string, deleteUserRow: any, index: number): void => {
        const eatonRole = row.roles.every((role: any) => role.roleName === UserRole.EatonAdmin);
        const ids = eatonRole ? [] : deleteUserRow.roles.map((item: any) => item['siteId']); // eslint-disable-line @typescript-eslint/no-explicit-any

        props.deleteClick({ show: true, userId: userId, siteIds: ids, index: index, userEmail: row.email });
    };
    const NavigateToEditUser = (userId: string): void => {
        pushAppRoute(history, { type: 'User', orgId: currentAppRoute.orgId, userId: userId });
    };

    const eatonAdminLoggedIn = (deleteUserId: string): boolean => {
        const isEatonAdmin = isRecordFound(userRole) && userRole.value.find((x) => x.roleName === UserRole.EatonAdmin);
        return (isEatonAdmin && deleteUserId === props.loggedInUser) || false;
    };

    const hasDeleteAccess = isRecordFound(userRole)
        ? userRole.value.some((x) => x.operations.some((operation) => operation === UserOperations.DeleteUser))
        : false;

    const hoverOnAction = (value: boolean): void => {
        setShowActions(value);
    };

    return (
        <React.Fragment>
            <TableRow
                data-testid={`row${props.index}`}
                classes={{ hover: classes.hover }}
                className={classes.tableRow}
                hover
                tabIndex={-1}
                key={row.userId}
                component="tr"
                onMouseEnter={(): void => {
                    hoverOnAction(true);
                }}
                onMouseLeave={(): void => {
                    hoverOnAction(false);
                }}
            >
                <TableCell style={{ width: '3%', padding: 10 }}>
                    <Avatar
                        style={{
                            backgroundColor: PXBColors.blue[50],
                            color: PXBColors.blue[500],
                        }}
                    >
                        {row.firstName && row.firstName.charAt(0)}
                        {row.lastName && row.lastName.charAt(0)}
                    </Avatar>
                </TableCell>
                <TableCell
                    id={labelId}
                    align="left"
                    padding="checkbox"
                    className={userClasses.WordBreak}
                    style={{ width: '20%' }}
                >
                    {row.firstName} {row.lastName}
                </TableCell>
                <TableCell align="left" padding="checkbox" style={{ width: '15%' }}>
                    {row.role}
                </TableCell>
                <TableCell align="left" padding="checkbox" style={{ width: '20%', padding: 0, paddingLeft: 12 }}>
                    {row.location}
                </TableCell>

                <TableCell
                    align="left"
                    padding="checkbox"
                    style={{ width: '30%', padding: 0, paddingLeft: 12 }}
                    className={userClasses.WordBreak}
                >
                    {row.email}
                </TableCell>

                <TableCell align="left" style={{ width: '2%', paddingRight: 0 }}>
                    {row.role === UserRole.Unassigned ? (
                        <StatusTag status={Status.Unassigned} />
                    ) : row.status === 'Active' ? (
                        <StatusTag status={Status.Active} />
                    ) : (
                        <StatusTag status={Status.InActive} />
                    )}
                </TableCell>
                <TableCell padding="checkbox" style={{ width: '2%', padding: 0 }}>
                    {row.roles.length > 1 && (
                        <IconButton size="small" onClick={(): void => setOpen(!open)}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell style={{ width: '5%' }}>
                    <Box>
                        {props.index === props.deleteIndex &&
                        registrationState.deleteUserLoading &&
                        !registrationState.deleteUserSuccess ? (
                            <CircularProgress size="2rem" />
                        ) : (
                            <Box
                                style={{ display: 'flex', flexDirection: 'row', flex: 1 }}
                                className={showActions ? classes.iconVisibility : classes.iconVisibilityHidden}
                            >
                                {!eatonAdminLoggedIn(row.userId) && hasDeleteAccess && !(props.loggedInUser === row.userId) &&(
                                    <IconButton
                                        data-testid="delete-action" 
                                        size="small"
                                        onClick={(): void => handleDelete(row.userId, row, props.index)}
                                    >
                                        <DeleteIcon style={{ color: PXBColors.red[500] }} />
                                    </IconButton>
                                )}
                                {!(props.loggedInUser === row.userId) &&(
                                <IconButton
                                    component="button"
                                    size="small"
                                    style={{ marginLeft: 5 }}
                                    onClick={(): void => NavigateToEditUser(row.userId)}
                                    data-testid="more-icon"
                                >
                                    <RemoveRedEyeIcon fontSize="small" />
                                </IconButton>
                             ) }
                            </Box>
                        )}
                    </Box>
                    {!showActions && <Box></Box>}
                </TableCell>
            </TableRow>
            {row.roles.length > 1 && (
                <TableRow className={classes.tableRow} key={`${row.userId}${props.index}`}>
                    <TableCell style={{ padding: 0 }} colSpan={9}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box margin={1}>
                                <Table size="small">
                                    <TableBody>
                                        {row.roles.map(
                                            (role: {
                                                roleId: string;
                                                roleName: string;
                                                siteId: string;
                                                siteName: string;
                                            }) => (
                                                <TableRow
                                                    key={`${role.siteId}${row.userId}`}
                                                    style={{ backgroundColor: '#ffffff' }}
                                                >
                                                    <TableCell
                                                        align="left"
                                                        padding="checkbox"
                                                        style={{ width: '23%', padding: 0 }}
                                                        colSpan={2}
                                                    >
                                                        &nbsp;
                                                    </TableCell>

                                                    <TableCell
                                                        align="left"
                                                        padding="normal"
                                                        style={{ width: '15%', padding: 0, paddingLeft: 5 }}
                                                    >
                                                        {role.roleName}
                                                    </TableCell>
                                                    <TableCell
                                                        align="left"
                                                        padding="normal"
                                                        style={{ width: '20%', paddingLeft: 0 }}
                                                    >
                                                        {role.siteName}
                                                    </TableCell>
                                                    <TableCell padding="checkbox">&nbsp;</TableCell>
                                                    <TableCell padding="checkbox">&nbsp;</TableCell>
                                                    <TableCell padding="checkbox"></TableCell>
                                                    <TableCell align="left"></TableCell>
                                                    <TableCell align="left"></TableCell>
                                                </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </React.Fragment>
    );
};
